import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Scissors, LogOut, Calendar, DollarSign,
    Clock, CheckCircle, XCircle, Edit, Trash2, Plus, Search, Eye, Package
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import { formatAppointmentDate, getLocalDateString, getTimeSlotDisplayItems } from '../utils/dateUtils';
import { getVisiblePageNumbers } from '../utils/pagination';
import './BarberDashboard.css';

const BarberDashboard = () => {
    const [barber, setBarber] = useState(null);
    const [stats, setStats] = useState({
        appointments_today: 0,
        weekly_revenue: 0,
        rating: 0
    });
    const [appointments, setAppointments] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    // Filtro por día de la semana (0=Dom..6=Sáb, 'all'=todas) y búsqueda en la página actual
    const [appointmentDayFilter, setAppointmentDayFilter] = useState('all');
    const [appointmentSearchQuery, setAppointmentSearchQuery] = useState('');
    const [appointmentsPage, setAppointmentsPage] = useState(1);
    const [appointmentsTotal, setAppointmentsTotal] = useState(0);
    const PAGE_SIZE = 20;
    /** Último total conocido por respuesta correcta con `total` numérico; evita reset de página por errores o formato antiguo. */
    const lastReliableAppointmentsTotalRef = useRef(0);

    // States para edición de duración
    const [editingDurationId, setEditingDurationId] = useState(null);
    const [tempDuration, setTempDuration] = useState('');

    // States para modales de confirmación
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'complete' o 'cancel'
    const [appointmentToUpdate, setAppointmentToUpdate] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Estado para editar y eliminar citas
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [editForm, setEditForm] = useState({
        appointment_date: '',
        appointment_time: '',
        duration_minutes: '',
        service_name: '',
        price: '',
        notes: ''
    });

    const [appointmentInventoryLines, setAppointmentInventoryLines] = useState([]);
    const [inventoryCatalog, setInventoryCatalog] = useState([]);
    const [inventoryCategories, setInventoryCategories] = useState([]);
    const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState('');
    const [loadingAppointmentInventory, setLoadingAppointmentInventory] = useState(false);

    // Estado para agendar citas (como admin)
    const [showBookModal, setShowBookModal] = useState(false);
    const [bookStep, setBookStep] = useState('service'); // 'service' | 'datetime'
    const [bookServices, setBookServices] = useState([]);
    const [selectedBookService, setSelectedBookService] = useState(null);
    const [bookForm, setBookForm] = useState({
        client_name: '',
        client_phone: '',
        client_email: '',
        appointment_date: '',
        appointment_time: '',
        duration_minutes: 30,
        price: '',
        notes: ''
    });
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [loadingBookServices, setLoadingBookServices] = useState(false);
    const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

    // Vista móvil: modal de detalle al pulsar "Ver"
    const [viewDetailAppointment, setViewDetailAppointment] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    const handleSaveDuration = async (appointmentId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/barber/appointments/${appointmentId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ duration_minutes: parseInt(tempDuration) })
            });

            if (response.ok) {
                // Actualizar localmente para feedback inmediato
                setAppointments(prev => prev.map(apt =>
                    apt.id === appointmentId
                        ? { ...apt, duration_minutes: parseInt(tempDuration) }
                        : apt
                ));
                setEditingDurationId(null);
                // Opcional: recargar todo para asegurar consistencia
                // loadData(); 
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Error al actualizar la duración');
            }
        } catch (error) {
            console.error('Error saving duration:', error);
            alert('Error al guardar la cambio de duración');
        }
    };

    // Cargar datos iniciales solo una vez
    useEffect(() => {
        checkAuth();
        loadInitialData();
    }, []);

    // Cargar citas (20 por página) cuando termina la carga inicial o cambia filtro/página
    useEffect(() => {
        if (!loading) {
            loadAppointments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, appointmentDayFilter, appointmentsPage]);

    useEffect(() => {
        if (appointmentsTotal <= 0) return;
        const totalPages = Math.max(1, Math.ceil(appointmentsTotal / PAGE_SIZE));
        if (appointmentsPage > totalPages) {
            setAppointmentsPage(totalPages);
        }
    }, [appointmentsTotal, appointmentsPage, PAGE_SIZE]);

    const checkAuth = () => {
        const token = localStorage.getItem('token');
        const userRole = localStorage.getItem('userRole');

        if (!token || userRole !== 'barber') {
            navigate('/login');
            return;
        }
    };

    // Cargar datos iniciales (solo stats; las citas se cargan en loadAppointments por página)
    const loadInitialData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }
            const headers = { 'Authorization': `Bearer ${token}` };
            const statsResponse = await fetch(`${API_BASE_URL}/barber/stats`, { headers });
            if (statsResponse.ok) {
                const statsData = await statsResponse.json();
                setBarber(statsData.barber);
                setStats({
                    appointments_today: statsData.appointments_today || 0,
                    weekly_revenue: statsData.weekly_revenue || 0,
                    rating: statsData.rating || 0
                });
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Cargar citas del barbero: 20 recientes por página, con filtro opcional por día de la semana
    const loadAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const token = localStorage.getItem('token');
            if (!token) return;
            const headers = { 'Authorization': `Bearer ${token}` };
            const offset = (appointmentsPage - 1) * PAGE_SIZE;
            let url = `${API_BASE_URL}/barber/appointments?limit=${PAGE_SIZE}&offset=${offset}`;
            if (appointmentDayFilter !== 'all') {
                url += `&day_of_week=${Number(appointmentDayFilter)}`;
            }
            const res = await fetch(url, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data && Array.isArray(data.appointments)) {
                    setAppointments(data.appointments);
                    if (typeof data.total === 'number' && data.total >= 0) {
                        lastReliableAppointmentsTotalRef.current = data.total;
                        setAppointmentsTotal(data.total);
                    } else if (lastReliableAppointmentsTotalRef.current > 0) {
                        setAppointmentsTotal(lastReliableAppointmentsTotalRef.current);
                    }
                } else if (Array.isArray(data)) {
                    setAppointments(data);
                    // API antigua (solo array): no usar data.length como total (sería ~20 y rompe todas las páginas).
                    if (lastReliableAppointmentsTotalRef.current > 0) {
                        setAppointmentsTotal(lastReliableAppointmentsTotalRef.current);
                    }
            } else {
                setAppointments([]);
                    setAppointmentsTotal(0);
                    lastReliableAppointmentsTotalRef.current = 0;
            }
            } else {
                console.warn('Error cargando citas (HTTP)', res.status);
                // Mantener lista y total para no saltar de vuelta a la página 1 por un fallo puntual.
            }
        } catch (error) {
            console.error('Error loading appointments:', error);
            // Mantener lista y total actuales
        } finally {
            setLoadingAppointments(false);
        }
    };

    // Función legacy para mantener compatibilidad
    const loadData = async () => {
        await loadInitialData();
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        navigate('/login');
    };

    const handleConfirmAction = (appointmentId, action) => {
        setAppointmentToUpdate(appointmentId);
        setConfirmAction(action);
        setShowConfirmModal(true);
    };

    const handleUpdateAppointment = async (appointmentId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/barber/appointments/${appointmentId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                setViewDetailAppointment(null);
                setShowConfirmModal(false);
                setAppointmentToUpdate(null);
                setConfirmAction(null);
                const actionText = newStatus === 'completed' ? 'completada' : 'cancelada';
                setSuccessMessage(`¡La cita ha sido ${actionText} exitosamente!`);
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                loadAppointments();
            } else {
                const errorData = await response.json();
                setShowConfirmModal(false);
                alert(errorData.detail || 'Error al actualizar la cita');
            }
        } catch (error) {
            console.error('Error:', error);
            setShowConfirmModal(false);
            alert('Error al actualizar la cita');
        }
    };

    const handleConfirm = () => {
        if (appointmentToUpdate && confirmAction) {
            const newStatus = confirmAction === 'complete' ? 'completed' : 'cancelled';
            handleUpdateAppointment(appointmentToUpdate, newStatus);
        }
    };

    const handleCancelModal = () => {
        setShowConfirmModal(false);
        setAppointmentToUpdate(null);
        setConfirmAction(null);
    };

    const addProductToAppointmentLine = (product) => {
        if (!product?.id) return;
        const id = product.id;
        const unit = parseFloat(product.price);
        const unitPrice = Number.isFinite(unit) ? unit : 0;
        setAppointmentInventoryLines(prev => {
            const idx = prev.findIndex(l => l.inventory_id === id);
            if (idx >= 0) {
                const next = [...prev];
                const cur = typeof next[idx].quantity === 'number'
                    ? next[idx].quantity
                    : parseInt(String(next[idx].quantity), 10);
                const base = Number.isFinite(cur) && cur >= 1 ? cur : 1;
                next[idx] = { ...next[idx], quantity: base + 1 };
                return next;
            }
            return [...prev, {
                inventory_id: id,
                quantity: 1,
                product_name: product.name || '',
                unit_price: unitPrice
            }];
        });
    };

    const updateAppointmentLineQuantity = (inventoryId, qty) => {
        const raw = String(qty);
        if (raw === '') {
            setAppointmentInventoryLines(prev => prev.map(l =>
                l.inventory_id === inventoryId ? { ...l, quantity: '' } : l
            ));
            return;
        }
        const q = parseInt(raw, 10);
        if (!Number.isFinite(q) || q < 1) return;
        setAppointmentInventoryLines(prev => prev.map(l =>
            l.inventory_id === inventoryId ? { ...l, quantity: q } : l
        ));
    };

    const normalizeAppointmentLineQuantityOnBlur = (inventoryId, rawValue) => {
        const q = parseInt(String(rawValue), 10);
        if (!Number.isFinite(q) || q < 1) {
            setAppointmentInventoryLines(prev => prev.map(l =>
                l.inventory_id === inventoryId ? { ...l, quantity: 1 } : l
            ));
        }
    };

    const removeAppointmentLine = (inventoryId) => {
        setAppointmentInventoryLines(prev => prev.filter(l => l.inventory_id !== inventoryId));
    };

    const reloadEditTimeSlots = async (date, durationOverride, appointmentId, { clearTime = false } = {}) => {
        if (clearTime) {
            setEditForm(prev => ({ ...prev, appointment_date: date, appointment_time: '' }));
        } else if (date) {
            setEditForm(prev => ({ ...prev, appointment_date: date }));
        }
        if (!date || !barber?.id || !appointmentId) return;
        setLoadingTimeSlots(true);
        try {
            const token = localStorage.getItem('token');
            const parsed = parseInt(String(durationOverride ?? editForm.duration_minutes ?? ''), 10);
            const duration = Number.isFinite(parsed) && parsed >= 5 ? parsed : 30;
            const excludeParam = `&exclude_appointment_id=${appointmentId}`;
            const url = `${API_BASE_URL}/barber/availability?barber_id=${barber.id}&date_str=${date}&duration=${duration}&use_extended=true${excludeParam}`;
            const res = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (res.ok) {
                const slots = await res.json();
                setAvailableTimeSlots(slots || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingTimeSlots(false);
        }
    };

    const handleEditDateChange = async (date) => {
        await reloadEditTimeSlots(date, undefined, editingAppointment?.id, { clearTime: true });
    };

    const handleOpenEdit = async (appointment) => {
        setViewDetailAppointment(null);
        const dateStr = (appointment.appointment_date || '').toString().split('T')[0];
        const timeStr = (appointment.appointment_time || '').toString().substring(0, 5);
        const svc = appointment.service || {};
        const rawSp = appointment.service_price;
        const initialPrice = rawSp != null && rawSp !== ''
            ? String(rawSp)
            : (svc.price != null && svc.price !== '' ? String(svc.price) : '');
        setEditingAppointment(appointment);
        setEditForm({
            appointment_date: dateStr,
            appointment_time: timeStr,
            duration_minutes: String(appointment.duration_minutes ?? svc.duration_minutes ?? 30),
            service_name: svc.name || appointment.service_name || '',
            price: initialPrice,
            notes: appointment.notes || ''
        });
        setInventoryCategoryFilter('');
        setLoadingAppointmentInventory(true);
        setAppointmentInventoryLines([]);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const [itemsRes, invRes, catRes] = await Promise.all([
                fetch(`${API_BASE_URL}/barber/appointments/${appointment.id}/inventory-items`, { headers }),
                fetch(`${API_BASE_URL}/barber/inventory`, { headers }),
                fetch(`${API_BASE_URL}/barber/inventory/categories`, { headers })
            ]);
            if (invRes.ok) {
                const invData = await invRes.json();
                setInventoryCatalog(Array.isArray(invData) ? invData : []);
            }
            if (catRes.ok) {
                const catData = await catRes.json();
                setInventoryCategories(Array.isArray(catData) ? catData : []);
            }
            if (itemsRes.ok) {
                const items = await itemsRes.json();
                const arr = Array.isArray(items) ? items : [];
                setAppointmentInventoryLines(arr.map(it => ({
                    inventory_id: it.inventory_id,
                    quantity: it.quantity || 1,
                    product_name: it.product_name || '',
                    unit_price: parseFloat(it.unit_price) || 0
                })));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingAppointmentInventory(false);
        }
        setShowEditModal(true);
        const duration = parseInt(String(appointment.duration_minutes ?? svc.duration_minutes ?? 30), 10) || 30;
        if (dateStr && barber?.id) {
            await reloadEditTimeSlots(dateStr, duration, appointment.id, { clearTime: false });
        }
        if (services.length === 0) {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_BASE_URL}/barber/my-services`, { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    const list = await res.json();
                    setServices(list || []);
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setEditingAppointment(null);
        setAppointmentInventoryLines([]);
        setInventoryCategoryFilter('');
        setAvailableTimeSlots([]);
        setEditForm({
            appointment_date: '',
            appointment_time: '',
            duration_minutes: '',
            service_name: '',
            price: '',
            notes: ''
        });
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        if (!editingAppointment) return;
        if (!editForm.appointment_time) {
            alert('Selecciona una hora para la cita.');
            return;
        }
        for (const l of appointmentInventoryLines) {
            const q = parseInt(String(l.quantity), 10);
            if (!Number.isFinite(q) || q < 1) {
                alert('Indica una cantidad válida (mínimo 1) en cada producto, o quita la línea.');
                return;
            }
        }
        try {
            const token = localStorage.getItem('token');
            const matchedSvc = services.find(s => s.name === editForm.service_name);
            const parsedPrice = parseFloat(String(editForm.price ?? '').replace(',', '.'), 10);
            const pricePayload = Number.isFinite(parsedPrice) ? parsedPrice : (matchedSvc?.price != null ? Number(matchedSvc.price) : undefined);
            const payload = {
                appointment_date: editForm.appointment_date,
                appointment_time: editForm.appointment_time,
                duration_minutes: parseInt(editForm.duration_minutes, 10) || 30,
                notes: editForm.notes,
                inventory_lines: appointmentInventoryLines.map(l => ({
                    inventory_id: l.inventory_id,
                    quantity: parseInt(String(l.quantity), 10)
                }))
            };
            if (editForm.service_name) payload.service_name = editForm.service_name;
            if (matchedSvc?.id) payload.service_id = matchedSvc.id;
            payload.price = pricePayload !== undefined ? pricePayload : 0;
            const response = await fetch(`${API_BASE_URL}/barber/appointments/${editingAppointment.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                handleCloseEdit();
                setSuccessMessage('Cita actualizada correctamente');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                loadAppointments();
            } else {
                const err = await response.json();
                alert(err.detail || 'Error al actualizar la cita');
            }
        } catch (error) {
            console.error('Error updating appointment:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleOpenBook = async () => {
        setShowBookModal(true);
        setBookStep('service');
        setSelectedBookService(null);
        setBookForm({ client_name: '', client_phone: '', client_email: '', appointment_date: '', appointment_time: '', duration_minutes: 30, price: '', notes: '' });
        setAvailableTimeSlots([]);
        setLoadingBookServices(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/barber/my-services`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const list = await res.json();
                setBookServices(list || []);
            }
        } catch (e) { console.error(e); }
        finally { setLoadingBookServices(false); }
    };

    const handleCloseBook = () => {
        setShowBookModal(false);
        setBookStep('service');
        setSelectedBookService(null);
        setBookServices([]);
        setBookForm({ client_name: '', client_phone: '', client_email: '', appointment_date: '', appointment_time: '', duration_minutes: 30, price: '', notes: '' });
        setAvailableTimeSlots([]);
    };

    const handleSelectBookService = (service) => {
        setSelectedBookService(service);
        setBookForm(prev => ({
            ...prev,
            duration_minutes: service.duration_minutes || 30,
            price: service.price != null ? String(service.price) : '',
        }));
        setBookStep('datetime');
    };

    const handleBookDateChange = async (date, durationOverride) => {
        setBookForm(prev => ({ ...prev, appointment_date: date, appointment_time: '' }));
        if (date && barber?.id && selectedBookService) {
            setLoadingTimeSlots(true);
            try {
                const token = localStorage.getItem('token');
                const duration = durationOverride ?? bookForm.duration_minutes ?? selectedBookService.duration_minutes ?? 30;
                const url = `${API_BASE_URL}/barber/availability?barber_id=${barber.id}&date_str=${date}&duration=${duration}&use_extended=true`;
                const res = await fetch(url, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                if (res.ok) {
                    const slots = await res.json();
                    setAvailableTimeSlots(slots || []);
                }
            } catch (e) { console.error(e); }
            finally { setLoadingTimeSlots(false); }
        }
    };

    const handleSubmitBook = async (e) => {
        e.preventDefault();
        if (!barber?.id || !selectedBookService) return;
        if (!bookForm.appointment_time) {
            alert('Selecciona una hora para la cita.');
            return;
        }
        const parsedDuration = parseInt(String(bookForm.duration_minutes ?? ''), 10);
        const durationForPayload = Number.isFinite(parsedDuration) && parsedDuration >= 5
            ? parsedDuration
            : (selectedBookService.duration_minutes || 30);
        const parsedPrice = parseFloat(String(bookForm.price ?? '').replace(',', '.'));
        const priceForPayload = Number.isFinite(parsedPrice) ? parsedPrice : (selectedBookService.price ?? null);
        try {
            const payload = {
                barber_id: barber.id,
                client_name: bookForm.client_name,
                client_phone: bookForm.client_phone,
                client_email: bookForm.client_email || '',
                service_name: selectedBookService.name,
                service_id: selectedBookService.id || null,
                appointment_date: bookForm.appointment_date,
                appointment_time: bookForm.appointment_time,
                duration_minutes: durationForPayload,
                price: priceForPayload,
                notes: bookForm.notes
            };
            const response = await fetch(`${API_BASE_URL}/barber/book-appointment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                handleCloseBook();
                setSuccessMessage('Cita agendada exitosamente');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                loadAppointments();
            } else {
                const err = await response.json();
                alert(err.detail || 'Error al agendar la cita');
            }
        } catch (error) {
            console.error('Error booking:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const handleDeleteAppointment = async (appointmentId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer.')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/barber/appointments/${appointmentId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                setViewDetailAppointment(null);
                setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
                setSuccessMessage('Cita eliminada correctamente');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
            } else {
                const err = await response.json();
                alert(err.detail || 'Error al eliminar la cita');
            }
        } catch (error) {
            console.error('Error deleting appointment:', error);
            alert('Error al conectar con el servidor');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const getInitials = (name) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 3);
    };

        const statusColors = {
            'confirmed': '#10b981',
            'pending': '#f59e0b',
            'completed': '#3b82f6',
            'cancelled': '#ef4444'
        };
    const getStatusLabel = (status) =>
        status === 'confirmed' ? 'Confirmada' :
        status === 'pending' ? 'Pendiente' :
        status === 'completed' ? 'Completada' : 'Cancelada';
    const formatTime = (t) => (t || '').toString().substring(0, 5) || 'N/A';

    const getAppointmentDisplayTotal = (apt) => {
        if (!apt) return 0;
        if (apt.appointment_total != null && apt.appointment_total !== '') return Number(apt.appointment_total);
        const svc = apt.service || {};
        const sp = Number(apt.service_price);
        const inv = Number(apt.inventory_subtotal);
        if (Number.isFinite(sp) || Number.isFinite(inv)) {
            return (Number.isFinite(sp) ? sp : Number(svc.price || 0)) + (Number.isFinite(inv) ? inv : 0);
        }
        return Number(svc.price || 0);
    };

    if (loading) {
        return (
            <div className="barber-dashboard">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Cargando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="barber-dashboard">
            <header className="barber-header">
                <div className="header-left">
                    <div className="logo">
                        <Scissors size={28} />
                        <span>GENUINO'S</span>
                    </div>
                </div>
                <div className="header-right">
                    <div className="barber-profile">
                        <div className="profile-avatar">
                            {barber?.name ? getInitials(barber.name) : 'B'}
                        </div>
                        <div className="profile-info">
                            <span className="profile-name">{barber?.name || 'Barbero'}</span>
                            <span className="profile-specialty">{barber?.specialty || 'Barbero'}</span>
                        </div>
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        <LogOut size={18} />
                        Salir
                    </button>
                </div>
            </header>

            <div className="barber-content">
                <div className="welcome-section">
                    <h1>
                        Bienvenido, <span className="barber-name-highlight">{barber?.name || 'Barbero'}</span>
                    </h1>
                    <p>Tu agenda del día y estadísticas de rendimiento</p>
                </div>

                <div className="appointments-section">
                    <div className="appointments-header">
                        <h2 className="appointments-section-title">Mis citas</h2>
                        <button className="add-appointment-btn" onClick={handleOpenBook}>
                            <Plus size={20} />
                            Agendar citas
                        </button>
                    </div>

                    {(() => {
                        const DAYS_LABELS = [
                            { value: 'all', label: 'Todas' },
                            { value: 0, label: 'Dom' },
                            { value: 1, label: 'Lun' },
                            { value: 2, label: 'Mar' },
                            { value: 3, label: 'Mié' },
                            { value: 4, label: 'Jue' },
                            { value: 5, label: 'Vie' },
                            { value: 6, label: 'Sáb' }
                        ];
                        const getDayOfWeek = (dateStr) => {
                            if (!dateStr) return -1;
                            const d = new Date(dateStr + 'T12:00:00');
                            return d.getDay();
                        };
                        const searchLower = appointmentSearchQuery.trim().toLowerCase();
                        const dayFilter = appointmentDayFilter === 'all' ? null : Number(appointmentDayFilter);
                        const totalPages = Math.max(1, Math.ceil(appointmentsTotal / PAGE_SIZE));
                        const pageNumbers = getVisiblePageNumbers(appointmentsPage, totalPages);
                        const filteredAppointments = appointments.filter((apt) => {
                            if (dayFilter !== null && getDayOfWeek(apt.appointment_date) !== dayFilter) return false;
                            if (!searchLower) return true;
                            const client = apt.client || {};
                            const name = (client.name || '').toLowerCase();
                            const email = (client.email || '').toLowerCase();
                            const phone = (client.phone || '').replace(/\s/g, '');
                            const queryNorm = searchLower.replace(/\s/g, '');
                            return name.includes(searchLower) || email.includes(searchLower) || phone.includes(queryNorm);
                        });
                        const renderAppointmentsPagination = (placement) => {
                            if (totalPages <= 1) return null;
                            return (
                                <div
                                    className={`appointments-pagination${placement === 'top' ? ' appointments-pagination-top' : ' appointments-pagination-bottom'}`}
                                >
                                    <span className="pagination-info">
                                        Página {appointmentsPage} de {totalPages}
                                        {appointmentsTotal > 0 && ` (${appointmentsTotal} citas)`}
                                        {filteredAppointments.length > 0 && (
                                            <> · Mostrando {filteredAppointments.length} en esta página</>
                                        )}
                                    </span>
                                    <div className="pagination-controls-row">
                                        <button
                                            type="button"
                                            className="pagination-btn"
                                            disabled={appointmentsPage <= 1 || loadingAppointments}
                                            onClick={() => setAppointmentsPage(p => Math.max(1, p - 1))}
                                        >
                                            Anterior
                                        </button>
                                        <div className="pagination-pages" role="navigation" aria-label="Páginas">
                                            {pageNumbers.map((item, idx) =>
                                                item === 'ellipsis' ? (
                                                    <span key={`${placement}-ellipsis-${idx}`} className="pagination-ellipsis" aria-hidden>…</span>
                                                ) : (
                                                    <button
                                                        key={`${placement}-p-${item}`}
                                                        type="button"
                                                        className={`pagination-page-num${item === appointmentsPage ? ' active' : ''}`}
                                                        disabled={loadingAppointments}
                                                        onClick={() => setAppointmentsPage(item)}
                                                        aria-current={item === appointmentsPage ? 'page' : undefined}
                                                    >
                                                        {item}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="pagination-btn"
                                            disabled={appointmentsPage >= totalPages || loadingAppointments}
                                            onClick={() => setAppointmentsPage(p => Math.min(totalPages, p + 1))}
                                        >
                                            Siguiente
                                        </button>
                                    </div>
                                </div>
                            );
                        };
                        return (
                            <>
                                <div className="appointments-filters-bar">
                                    <div className="appointments-search-wrap">
                                        <Search size={18} className="appointments-search-icon" />
                                        <input
                                            type="text"
                                            className="appointments-search-input"
                                            placeholder="Buscar por nombre, correo o teléfono..."
                                            value={appointmentSearchQuery}
                                            onChange={(e) => setAppointmentSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <div className="appointments-days-tabs">
                                        {DAYS_LABELS.map(({ value, label }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`appointments-day-tab ${appointmentDayFilter === value ? 'active' : ''}`}
                                                onClick={() => {
                                                    setAppointmentDayFilter(value);
                                                    setAppointmentsPage(1);
                                                    lastReliableAppointmentsTotalRef.current = 0;
                                                }}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {renderAppointmentsPagination('top')}

                                <div className="appointments-table-wrap">
                                    {loadingAppointments ? (
                                        <div className="loading-appointments">
                                            <div className="loading-spinner-small"></div>
                                            <p>Cargando citas...</p>
                                        </div>
                                    ) : filteredAppointments.length > 0 ? (
                                        <div className="appointments-table-inner">
                                        {isMobile ? (
                                            <table className="appointments-table appointments-table-mobile">
                                                <thead>
                                                    <tr>
                                                        <th>Nombre</th>
                                                        <th>Fecha / Hora</th>
                                                        <th>Estado</th>
                                                        <th>Servicio</th>
                                                        <th>Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredAppointments.map((appointment) => {
                                                        const client = appointment.client || {};
                                                        const service = appointment.service || {};
                                                        return (
                                                            <tr key={appointment.id}>
                                                                <td className="td-client">
                                                                    <span className="table-client-name">{client.name || 'Cliente'}</span>
                                                                </td>
                                                                <td className="td-fecha-mobile">
                                                                    <span className="td-fecha-mobile-date">
                                                                        {appointment.appointment_date
                                                                            ? formatAppointmentDate(appointment.appointment_date)
                                                                            : '—'}
                                                                    </span>
                                                                    <span className="td-fecha-mobile-time">
                                                                        {formatTime(appointment.appointment_time)}
                                                                    </span>
                                                                </td>
                                                                <td className="td-estado">
                                                                    <span
                                                                        className="table-status-badge"
                                                                        style={{
                                                                            backgroundColor: statusColors[appointment.status] || '#6b7280',
                                                                            color: 'white'
                                                                        }}
                                                                    >
                                                                        {getStatusLabel(appointment.status)}
                                                                    </span>
                                                                </td>
                                                                <td className="td-servicio">{service.name || 'N/A'}</td>
                                                                <td className="td-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="action-btn-ver"
                                                                        onClick={() => setViewDetailAppointment(appointment)}
                                                                        title="Ver detalle"
                                                                    >
                                                                        <Eye size={18} />
                                                                        Ver
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        ) : (
                                        <table className="appointments-table">
                                            <colgroup>
                                                <col className="col-cliente" />
                                                <col className="col-telefono" />
                                                <col className="col-correo" />
                                                <col className="col-estado" />
                                                <col className="col-fecha" />
                                                <col className="col-servicio" />
                                                <col className="col-hora" />
                                                <col className="col-duracion" />
                                                <col className="col-precio" />
                                                <col className="col-acciones" />
                                            </colgroup>
                                            <thead>
                                                <tr>
                                                    <th className="th-cliente">Cliente</th>
                                                    <th className="th-telefono">Teléfono</th>
                                                    <th className="th-correo">Correo</th>
                                                    <th className="th-estado">Estado</th>
                                                    <th className="th-fecha">Fecha</th>
                                                    <th className="th-servicio">Servicio</th>
                                                    <th className="th-hora">Hora</th>
                                                    <th className="th-duracion">Duración</th>
                                                    <th className="th-precio">Total</th>
                                                    <th className="th-acciones">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredAppointments.map((appointment) => {
                                                    const client = appointment.client || {};
                                                    const service = appointment.service || {};
                                                    return (
                                                        <tr key={appointment.id}>
                                                            <td className="td-client">
                                                                <span className="table-client-name">{client.name || 'Cliente'}</span>
                                                            </td>
                                                            <td className="td-telefono">{client.phone || '—'}</td>
                                                            <td className="td-email">{client.email || '—'}</td>
                                                            <td className="td-estado">
                                                                <span
                                                                    className="table-status-badge"
                                                                    style={{
                                                                        backgroundColor: statusColors[appointment.status] || '#6b7280',
                                                                        color: 'white'
                                                                    }}
                                                                >
                                                                    {getStatusLabel(appointment.status)}
                                                                </span>
                                                            </td>
                                                            <td className="td-fecha">
                                                                {appointment.appointment_date
                                                                    ? formatAppointmentDate(appointment.appointment_date)
                                                                    : 'N/A'}
                                                            </td>
                                                            <td className="td-servicio">{service.name || 'N/A'}</td>
                                                            <td className="td-hora">{formatTime(appointment.appointment_time)}</td>
                                                            <td className="td-duracion">
                                                                {editingDurationId === appointment.id ? (
                                                                    <div className="duration-edit-container">
                                                                        <input
                                                                            type="number"
                                                                            className="duration-input"
                                                                            value={tempDuration}
                                                                            onChange={(e) => setTempDuration(e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') handleSaveDuration(appointment.id);
                                                                                if (e.key === 'Escape') setEditingDurationId(null);
                                                                            }}
                                                                            autoFocus
                                                                            min="1"
                                                                        />
                                                                        <button
                                                                            className="save-duration-btn"
                                                                            onClick={() => handleSaveDuration(appointment.id)}
                                                                            title="Guardar"
                                                                        >
                                                                            <CheckCircle size={14} />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="duration-display"
                                                                        onClick={() => {
                                                                            if (appointment.status !== 'completed' && appointment.status !== 'cancelled') {
                                                                                setEditingDurationId(appointment.id);
                                                                                setTempDuration(appointment.duration_minutes || service.duration_minutes || 30);
                                                                            }
                                                                        }}
                                                                        title={appointment.status !== 'completed' && appointment.status !== 'cancelled' ? 'Clic para editar' : ''}
                                                                        style={{ cursor: appointment.status !== 'completed' && appointment.status !== 'cancelled' ? 'pointer' : 'default' }}
                                                                    >
                                                                        <span>{appointment.duration_minutes || service.duration_minutes || 'N/A'} min</span>
                                                                        {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                                                                            <Edit size={12} className="edit-icon" />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="td-precio">{formatCurrency(getAppointmentDisplayTotal(appointment))}</td>
                                                            <td className="td-actions">
                                                                {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="action-btn-table complete"
                                                                            onClick={() => handleConfirmAction(appointment.id, 'complete')}
                                                                            title="Completar"
                                                                        >
                                                                            <CheckCircle size={16} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="action-btn-table cancel"
                                                                            onClick={() => handleConfirmAction(appointment.id, 'cancel')}
                                                                            title="Cancelar"
                                                                        >
                                                                            <XCircle size={16} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="action-btn-table edit"
                                                                            onClick={() => handleOpenEdit(appointment)}
                                                                            title="Editar cita"
                                                                        >
                                                                            <Edit size={16} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="action-btn-table delete"
                                                                            onClick={() => handleDeleteAppointment(appointment.id)}
                                                                            title="Eliminar cita"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {(appointment.status === 'completed' || appointment.status === 'cancelled') && (
                                                                    <button
                                                                        type="button"
                                                                        className="action-btn-table delete"
                                                                        onClick={() => handleDeleteAppointment(appointment.id)}
                                                                        title="Eliminar cita"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        )}
                                        {!isMobile && <p className="appointments-table-scroll-hint">Desliza horizontalmente para ver todas las columnas</p>}
                                        {renderAppointmentsPagination('bottom')}
                                        </div>
                                    ) : (
                                        <div className="empty-appointments">
                                            <Calendar size={48} />
                                            <p>
                                                {appointments.length === 0
                                                    ? 'No hay citas en esta sección'
                                                    : 'No hay citas que coincidan con la búsqueda'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Modal de Confirmación */}
            {showConfirmModal && (
                <div className="modal-overlay" onClick={handleCancelModal}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-header">
                            <h3>
                                {confirmAction === 'complete' 
                                    ? '¿Quieres terminar esta cita?' 
                                    : '¿Quieres cancelar esta cita?'}
                            </h3>
                        </div>
                        <div className="confirm-modal-body">
                            <p>
                                {confirmAction === 'complete'
                                    ? 'Esta acción marcará la cita como completada. ¿Estás seguro?'
                                    : 'Esta acción cancelará la cita. ¿Estás seguro?'}
                            </p>
                        </div>
                        <div className="confirm-modal-actions">
                            <button
                                className="confirm-btn-cancel"
                                onClick={handleCancelModal}
                            >
                                No, cancelar
                            </button>
                            <button
                                className={`confirm-btn-${confirmAction === 'complete' ? 'complete' : 'cancel-action'}`}
                                onClick={handleConfirm}
                            >
                                Sí, {confirmAction === 'complete' ? 'terminar' : 'cancelar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver detalle (móvil) */}
            {viewDetailAppointment && (
                <div className="modal-overlay" onClick={() => setViewDetailAppointment(null)}>
                    <div className="confirm-modal view-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                        <div className="confirm-modal-header">
                            <h3>Detalle de la cita</h3>
                            <button
                                type="button"
                                className="view-detail-close"
                                onClick={() => setViewDetailAppointment(null)}
                                aria-label="Cerrar"
                            >
                                ×
                            </button>
                        </div>
                        <div className="view-detail-body">
                            {(() => {
                                const apt = viewDetailAppointment;
                                const client = apt.client || {};
                                const service = apt.service || {};
                                return (
                                    <>
                                        <div className="view-detail-grid">
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Cliente</span>
                                                <span className="view-detail-value">{client.name || '—'}</span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Teléfono</span>
                                                <span className="view-detail-value">{client.phone || '—'}</span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Correo</span>
                                                <span className="view-detail-value">{client.email || '—'}</span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Estado</span>
                                                <span
                                                    className="table-status-badge"
                                                    style={{
                                                        backgroundColor: statusColors[apt.status] || '#6b7280',
                                                        color: 'white'
                                                    }}
                                                >
                                                    {getStatusLabel(apt.status)}
                                                </span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Fecha</span>
                                                <span className="view-detail-value">
                                                    {apt.appointment_date ? formatAppointmentDate(apt.appointment_date) : 'N/A'}
                                                </span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Servicio</span>
                                                <span className="view-detail-value">{service.name || 'N/A'}</span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Hora</span>
                                                <span className="view-detail-value">{formatTime(apt.appointment_time)}</span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Duración</span>
                                                <span className="view-detail-value">
                                                    {apt.duration_minutes || service.duration_minutes || 'N/A'} min
                                                </span>
                                            </div>
                                            <div className="view-detail-item">
                                                <span className="view-detail-label">Total</span>
                                                <span className="view-detail-value td-price">{formatCurrency(getAppointmentDisplayTotal(apt))}</span>
                                            </div>
                                        </div>
                                        <div className="view-detail-actions">
                                            {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="action-btn-table complete"
                                                        onClick={() => handleConfirmAction(apt.id, 'complete')}
                                                        title="Completar"
                                                    >
                                                        <CheckCircle size={18} />
                                                        Completar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn-table cancel"
                                                        onClick={() => handleConfirmAction(apt.id, 'cancel')}
                                                        title="Cancelar"
                                                    >
                                                        <XCircle size={18} />
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn-table edit"
                                                        onClick={() => handleOpenEdit(apt)}
                                                        title="Editar cita"
                                                    >
                                                        <Edit size={18} />
                                                        Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn-table delete"
                                                        onClick={() => handleDeleteAppointment(apt.id)}
                                                        title="Eliminar cita"
                                                    >
                                                        <Trash2 size={18} />
                                                        Eliminar
                                                    </button>
                                                </>
                                            )}
                                            {(apt.status === 'completed' || apt.status === 'cancelled') && (
                                                <button
                                                    type="button"
                                                    className="action-btn-table delete"
                                                    onClick={() => handleDeleteAppointment(apt.id)}
                                                    title="Eliminar cita"
                                                >
                                                    <Trash2 size={18} />
                                                    Eliminar
                                                </button>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Editar Cita */}
            {showEditModal && editingAppointment && (
                <div className="modal-overlay" onClick={handleCloseEdit}>
                    <div className="confirm-modal edit-appointment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', maxHeight: '92vh', overflowY: 'auto' }}>
                        <div className="confirm-modal-header">
                            <h3>Editar cita</h3>
                        </div>
                        <form onSubmit={handleSaveEdit} className="edit-appointment-form">
                            <div className="form-group">
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <Clock size={15} /> Duración (min)
                                </label>
                                <input
                                    type="number"
                                    min={5}
                                    max={240}
                                    step={5}
                                    value={editForm.duration_minutes}
                                    onChange={(e) => {
                                        const parsed = parseInt(e.target.value, 10);
                                        const val = isNaN(parsed) ? '' : parsed;
                                        setEditForm(prev => ({ ...prev, duration_minutes: val }));
                                        if (val >= 5 && editForm.appointment_date && editingAppointment?.id) {
                                            reloadEditTimeSlots(editForm.appointment_date, val, editingAppointment.id, { clearTime: false });
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const val = Math.max(5, parseInt(e.target.value, 10) || 5);
                                        setEditForm(prev => ({ ...prev, duration_minutes: String(val) }));
                                        if (editForm.appointment_date && editingAppointment?.id) {
                                            reloadEditTimeSlots(editForm.appointment_date, val, editingAppointment.id, { clearTime: false });
                                        }
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Fecha *</label>
                                <input
                                    type="date"
                                    value={editForm.appointment_date}
                                    onChange={(e) => handleEditDateChange(e.target.value)}
                                    required
                                    min={getLocalDateString()}
                                />
                            </div>
                            {editForm.appointment_date && (
                                <div className="form-group">
                                    <label>Hora *</label>
                                    {loadingTimeSlots ? (
                                        <p>Cargando horarios disponibles...</p>
                                    ) : availableTimeSlots.length > 0 ? (
                                        <div className="time-slots-grid">
                                            {getTimeSlotDisplayItems(availableTimeSlots, 5).map((item, idx) => (
                                                item.type === 'available' ? (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => setEditForm(prev => ({ ...prev, appointment_time: item.slot.time_24 }))}
                                                        className={`time-slot-btn ${editForm.appointment_time === item.slot.time_24 ? 'selected' : ''}`}
                                                        style={{
                                                            padding: '0.4rem 0.25rem',
                                                            border: `2px solid ${editForm.appointment_time === item.slot.time_24 ? '#ef4444' : '#e5e7eb'}`,
                                                            borderRadius: '6px',
                                                            background: editForm.appointment_time === item.slot.time_24 ? '#ef4444' : 'white',
                                                            color: editForm.appointment_time === item.slot.time_24 ? 'white' : '#1f2937',
                                                            cursor: 'pointer',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {item.slot.time_display}
                                                    </button>
                                                ) : (
                                                    <div
                                                        key={idx}
                                                        className="time-slot-occupied-block"
                                                        style={{
                                                            padding: '0.4rem 0.25rem',
                                                            border: '2px solid #f3f4f6',
                                                            borderRadius: '6px',
                                                            background: '#f9fafb',
                                                            color: '#9ca3af',
                                                            fontSize: '0.7rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gridColumn: 'span 2'
                                                        }}
                                                    >
                                                        {item.startDisplay} - {item.endDisplay} Ocupado
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ color: '#ef4444' }}>No hay horarios disponibles para esta fecha</p>
                                    )}
                                </div>
                            )}
                            {services.length > 0 && (
                                <div className="form-group">
                                    <label>Servicio</label>
                                    <select
                                        value={editForm.service_name}
                                        onChange={(e) => setEditForm({ ...editForm, service_name: e.target.value })}
                                    >
                                        {services.map(svc => (
                                            <option key={svc.id} value={svc.name}>{svc.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Precio servicio (COP)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={editForm.price}
                                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                                    required
                                    placeholder="Ej. 35000"
                                />
                                <small style={{ color: '#6b7280', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>
                                    Precio del servicio; el total con productos se muestra abajo.
                                </small>
                            </div>
                            <div className="form-group" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '0.25rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <Package size={16} aria-hidden /> Productos del inventario (opcional)
                                </label>
                                {loadingAppointmentInventory ? (
                                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Cargando productos…</p>
                                ) : (
                                    <>
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', display: 'block' }}>Categoría</span>
                                            <select
                                                value={inventoryCategoryFilter}
                                                onChange={(e) => setInventoryCategoryFilter(e.target.value)}
                                                style={{ width: '100%', marginTop: '0.25rem', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                            >
                                                <option value="">Todas las categorías</option>
                                                {inventoryCategories.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem' }}>
                                            {inventoryCatalog.filter((p) => p.active !== false && (!inventoryCategoryFilter || p.category === inventoryCategoryFilter)).length === 0 ? (
                                                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: 0 }}>No hay productos en esta categoría.</p>
                                            ) : (
                                                inventoryCatalog
                                                    .filter((p) => p.active !== false && (!inventoryCategoryFilter || p.category === inventoryCategoryFilter))
                                                    .map((p) => (
                                                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid #f3f4f6' }}>
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.name}</div>
                                                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                                    {formatCurrency(p.price || 0)} · Stock {p.stock ?? 0}
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => addProductToAppointmentLine(p)}
                                                                disabled={(p.stock ?? 0) < 1}
                                                                style={{
                                                                    flexShrink: 0,
                                                                    padding: '0.35rem 0.65rem',
                                                                    fontSize: '0.75rem',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #ef4444',
                                                                    background: 'white',
                                                                    color: '#ef4444',
                                                                    cursor: (p.stock ?? 0) < 1 ? 'not-allowed' : 'pointer',
                                                                    opacity: (p.stock ?? 0) < 1 ? 0.5 : 1
                                                                }}
                                                            >
                                                                Añadir
                                                            </button>
                                                        </div>
                                                    ))
                                            )}
                                        </div>
                                        {appointmentInventoryLines.length > 0 && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.35rem' }}>En esta cita</div>
                                                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                                                            <th style={{ padding: '0.25rem 0' }}>Producto</th>
                                                            <th>Cant.</th>
                                                            <th>Subt.</th>
                                                            <th />
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {appointmentInventoryLines.map((line) => (
                                                            <tr key={line.inventory_id}>
                                                                <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>{line.product_name || '—'}</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        inputMode="numeric"
                                                                        value={line.quantity === '' ? '' : line.quantity}
                                                                        onChange={(e) => updateAppointmentLineQuantity(line.inventory_id, e.target.value)}
                                                                        onBlur={() => normalizeAppointmentLineQuantityOnBlur(line.inventory_id, line.quantity)}
                                                                        style={{ width: '3.5rem', padding: '0.2rem' }}
                                                                    />
                                                                </td>
                                                                <td>{formatCurrency((line.quantity || 0) * (line.unit_price || 0))}</td>
                                                                <td>
                                                                    <button type="button" onClick={() => removeAppointmentLine(line.inventory_id)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}>Quitar</button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {(() => {
                                            const prodSub = appointmentInventoryLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
                                            const svc = parseFloat(String(editForm.price ?? '').replace(',', '.')) || 0;
                                            const grand = svc + prodSub;
                                            return (
                                                <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', fontSize: '0.875rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Servicio</span><span>{formatCurrency(svc)}</span></div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Productos</span><span>{formatCurrency(prodSub)}</span></div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: '0.35rem', paddingTop: '0.35rem', borderTop: '1px solid #e5e7eb' }}>
                                                        <span>Total cita</span><span style={{ color: '#ef4444' }}>{formatCurrency(grand)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        <small style={{ color: '#6b7280', fontSize: '0.72rem', marginTop: '0.35rem', display: 'block' }}>
                                            Al guardar se descuenta el stock y se registra el subtotal de productos.
                                        </small>
                                    </>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Notas</label>
                                <textarea
                                    rows="2"
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                    placeholder="Opcional"
                                />
                            </div>
                            <div className="confirm-modal-actions">
                                <button type="button" className="confirm-btn-cancel" onClick={handleCloseEdit}>
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="confirm-btn-complete"
                                    disabled={!editForm.appointment_time}
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    Guardar cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Agendar Cita */}
            {showBookModal && (
                <div className="modal-overlay" onClick={handleCloseBook}>
                    <div className="confirm-modal book-appointment-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="confirm-modal-header">
                            <h3>Agendar Nueva Cita</h3>
                        </div>

                        {bookStep === 'service' && (
                            <div className="book-modal-step">
                                <p className="book-modal-subtitle">Selecciona el servicio</p>
                                {loadingBookServices ? (
                                    <p style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Cargando servicios...</p>
                                ) : bookServices.length === 0 ? (
                                    <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                                        No tienes módulos asignados o no hay servicios en tus módulos. Contacta al administrador para que te asigne módulos de servicio.
                                    </p>
                                ) : (
                                    <div className="book-services-grid">
                                        {bookServices.map(svc => (
                                            <button
                                                key={svc.id}
                                                type="button"
                                                className={`book-service-card ${selectedBookService?.id === svc.id ? 'selected' : ''}`}
                                                onClick={() => handleSelectBookService(svc)}
                                            >
                                                <span className="book-service-name">{svc.name}</span>
                                                <span className="book-service-price">{formatCurrency(svc.price || 0)}</span>
                                                <span className="book-service-duration">{svc.duration_minutes || 30} min</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="confirm-modal-actions" style={{ marginTop: '1.5rem' }}>
                                    <button type="button" className="confirm-btn-cancel" onClick={handleCloseBook}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}

                        {bookStep === 'datetime' && selectedBookService && (
                            <form onSubmit={handleSubmitBook} className="edit-appointment-form book-form">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => { setBookStep('service'); setSelectedBookService(null); setBookForm({ ...bookForm, appointment_date: '', appointment_time: '' }); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 0 }}
                                    >
                                        ← Volver
                                    </button>
                                    <p className="book-modal-subtitle" style={{ margin: 0 }}>Servicio: <strong>{selectedBookService.name}</strong></p>
                                </div>

                                <div className="form-group">
                                    <label>Nombre del Cliente *</label>
                                    <input
                                        type="text"
                                        value={bookForm.client_name}
                                        onChange={(e) => setBookForm({ ...bookForm, client_name: e.target.value })}
                                        required
                                        placeholder="Nombre completo"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Teléfono *</label>
                                    <input
                                        type="tel"
                                        value={bookForm.client_phone}
                                        onChange={(e) => setBookForm({ ...bookForm, client_phone: e.target.value })}
                                        required
                                        placeholder="+57 300 123 4567"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email (Opcional)</label>
                                    <input
                                        type="email"
                                        value={bookForm.client_email}
                                        onChange={(e) => setBookForm({ ...bookForm, client_email: e.target.value })}
                                        placeholder="cliente@email.com"
                                    />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <Clock size={15} /> Duración del servicio (min)
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <input
                                            type="number"
                                            value={bookForm.duration_minutes}
                                            min={5}
                                            max={240}
                                            step={5}
                                            style={{ width: '100px', fontWeight: '600', textAlign: 'center' }}
                                            onChange={(e) => {
                                                const parsed = parseInt(e.target.value, 10);
                                                const val = isNaN(parsed) ? '' : parsed;
                                                setBookForm(prev => ({ ...prev, duration_minutes: val, appointment_time: '' }));
                                                setAvailableTimeSlots([]);
                                                if (val >= 5 && bookForm.appointment_date) {
                                                    handleBookDateChange(bookForm.appointment_date, val);
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const val = Math.max(5, parseInt(e.target.value, 10) || 5);
                                                setBookForm(prev => ({ ...prev, duration_minutes: val }));
                                                if (bookForm.appointment_date) {
                                                    handleBookDateChange(bookForm.appointment_date, val);
                                                }
                                            }}
                                        />
                                        <small style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                                            Por defecto: {selectedBookService.duration_minutes || 30} min
                                        </small>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <DollarSign size={15} /> Precio servicio (COP)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="100"
                                        value={bookForm.price}
                                        onChange={(e) => setBookForm(prev => ({ ...prev, price: e.target.value }))}
                                        placeholder={selectedBookService.price != null ? String(selectedBookService.price) : 'Ej. 35000'}
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.2rem', display: 'block' }}>
                                        Por defecto: {selectedBookService.price != null ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(selectedBookService.price) : 'Sin precio'}
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input
                                        type="date"
                                        value={bookForm.appointment_date}
                                        onChange={(e) => handleBookDateChange(e.target.value)}
                                        required
                                        min={getLocalDateString()}
                                    />
                                </div>
                                {bookForm.appointment_date && (
                                    <div className="form-group">
                                        <label>Hora *</label>
                                        {loadingTimeSlots ? (
                                            <p>Cargando horarios disponibles...</p>
                                        ) : availableTimeSlots.length > 0 ? (
                                            <div className="time-slots-grid">
                                                {getTimeSlotDisplayItems(availableTimeSlots, 5).map((item, idx) => (
                                                    item.type === 'available' ? (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={() => setBookForm({ ...bookForm, appointment_time: item.slot.time_24 })}
                                                            className={`time-slot-btn ${bookForm.appointment_time === item.slot.time_24 ? 'selected' : ''}`}
                                                            style={{
                                                                padding: '0.5rem',
                                                                border: `2px solid ${bookForm.appointment_time === item.slot.time_24 ? '#ef4444' : '#e5e7eb'}`,
                                                                borderRadius: '8px',
                                                                background: bookForm.appointment_time === item.slot.time_24 ? '#ef4444' : 'white',
                                                                color: bookForm.appointment_time === item.slot.time_24 ? 'white' : '#1f2937',
                                                                cursor: 'pointer',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            {item.slot.time_display}
                                                        </button>
                                                    ) : (
                                                        <div
                                                            key={idx}
                                                            className="time-slot-occupied-block"
                                                            style={{
                                                                padding: '0.5rem',
                                                                border: '2px solid #f3f4f6',
                                                                borderRadius: '8px',
                                                                background: '#f9fafb',
                                                                color: '#9ca3af',
                                                                fontSize: '0.75rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gridColumn: 'span 2'
                                                            }}
                                                            title="Ocupado"
                                                        >
                                                            {item.startDisplay} - {item.endDisplay} Ocupado
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                        ) : (
                                            <p style={{ color: '#ef4444' }}>No hay horarios disponibles para esta fecha</p>
                                        )}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Notas (Opcional)</label>
                                    <textarea
                                        rows="2"
                                        value={bookForm.notes}
                                        onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })}
                                        placeholder="Notas adicionales..."
                                    />
                                </div>
                                <div className="confirm-modal-actions" style={{ marginTop: '1.5rem' }}>
                                    <button type="button" className="confirm-btn-cancel" onClick={handleCloseBook}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="confirm-btn-complete" disabled={!bookForm.appointment_time}>
                                        Agendar Cita
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Mensaje de Éxito */}
            {showSuccessMessage && (
                <div className="success-message">
                    <CheckCircle size={20} />
                    <span>{successMessage}</span>
                </div>
            )}

        </div>
    );
};

export default BarberDashboard;

